// import { back } from "@/api/DB";

/**
 * 表格时间格式化
 * @param {string|number|Date} cellValue - 需要格式化的时间值
 * @param {string} type - 时间格式类型：
 *                     - 'date': 年-月-日（YYYY-MM-DD）
 *                     - 'time': 时:分:秒（HH:mm:ss）
 *                     - 'datetime': 年-月-日 时:分:秒（YYYY-MM-DD HH:mm:ss）
 *                     - 'month': 年-月（YYYY-MM）
 * @returns {string} 格式化后的时间字符串
 */
export function formatDateUS (timestamp, format = 'full-date-time') {
  const date = new Date(timestamp)

  if (isNaN(date.getTime())) {
    throw new Error('Invalid timestamp')
  }

  const options = {
    timeZone: 'America/New_York',
    hour12: true,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric'
  }

  // const formatter = new Intl.DateTimeFormat('en-US', options);
  const formatter = new Intl.DateTimeFormat(navigator.language || navigator.userLanguage, options)
  const parts = formatter.formatToParts(date)

  const dateParts = {}
  let dayPeriod = ''
  parts.forEach(part => {
    dateParts[part.type] = part.value
    if (part.type === 'dayPeriod') {
      dayPeriod = part.value
    }
  })

  const day = parseInt(dateParts.day)
  let suffix = 'th'
  if (day % 10 === 1 && day !== 11) {
    suffix = 'st'
  } else if (day % 10 === 2 && day !== 12) {
    suffix = 'nd'
  } else if (day % 10 === 3 && day !== 13) {
    suffix = 'rd'
  }

  switch (format) {
    case 'year-month':
      return `${dateParts.month} ${dateParts.year}`
    case 'year-month-day':
      return `${dateParts.month} ${day}${suffix}, ${dateParts.year}`
    case 'full-date':
      return `${dateParts.weekday}, ${dateParts.month} ${day}${suffix}, ${dateParts.year}`
    case 'date-time':
      return `${dateParts.month}/${dateParts.day}/${dateParts.year}, ${dateParts.hour}:${dateParts.minute} ${dayPeriod}`
    case 'time':
      return `${dateParts.hour}:${dateParts.minute} ${dayPeriod}`
    case 'short-time':
      return `${dateParts.hour}:${dateParts.minute} ${dayPeriod}`
    case 'full-date-time':
    default:
      return `${dateParts.weekday}, ${dateParts.month} ${day}${suffix}, ${dateParts.year}, ${dateParts.hour}:${dateParts.minute} ${dayPeriod}`
  }
}
export function formatDate (cellValue, type = 'yyyy-MM-dd') {
  if (cellValue == null || cellValue === '') return ''
  const date = new Date(cellValue)
  // console.log('cellValue,date',cellValue,date);
  // 基础时间组件
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  // 英文月份和星期缩写
  const enMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const enWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  switch (type) {
    // 原有格式
    case 'yyyy-MM-dd':
      return `${year}-${month}-${day}`
    case 'HH:mm:ss':
      return `${hours}:${minutes}:${seconds}`
    case 'yyyy-MM-dd HH:mm:ss':
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    case 'yyyy-MM-dd HH:mm':
      return `${year}-${month}-${day} ${hours}:${minutes}`
    case 'MM-dd HH:mm':
      return `${month}-${day} ${hours}:${minutes}`
    case 'yyyy-MM':
      return `${year}-${month}`
    case 'yyyy年MM月dd日':
      return `${year}年${month}月${day}日`
    case 'MM-dd':
      return `${month}-${day}`
    case 'MM月dd日':
      return `${month}月${day}日`
    case 'HH时mm分ss秒':
      return `${hours}时${minutes}分${seconds}秒`
    case 'yyyy年MM月dd日 HH时mm分ss秒':
      return `${year}年${month}月${day}日 ${hours}时${minutes}分${seconds}秒`
    case 'yyyy年MM月':
      return `${year}年${month}月`
    case 'HH:mm':
      return `${hours}:${minutes}`

      // 新增英文格式
    case 'Week, DD Mon YYYY': // 如 "Sun, 22 Jun 2025"
      return `${enWeekdays[date.getDay()]}, ${day} ${enMonths[date.getMonth()]} ${year}`
    case 'Mon DD, YYYY': // 如 "Jun 22, 2025"
      return `${enMonths[date.getMonth()]} ${day}, ${year}`
    case 'Week, DD Mon YYYY h:mm PM': // 如 "Sun, 22 Jun 2025 3:00 PM"
      const hour12 = date.getHours() % 12 || 12
      const ampm = date.getHours() < 12 ? 'AM' : 'PM'
      return `${enWeekdays[date.getDay()]}, ${day} ${enMonths[date.getMonth()]} ${year} ${hour12}:${minutes} ${ampm}`
    case 'YYYY-MM-DD h:mm PM': // 如 "2025-10-01 10:00 AM"
      const hour12s = date.getHours() % 12 || 12
      const ampms = date.getHours() < 12 ? 'AM' : 'PM'
      return `${year}-${month}-${day} ${hour12s}:${minutes} ${ampms}`
    case 'h:mm PM': // 如 "3:00 PM"
      const hour12Short = date.getHours() % 12 || 12
      const ampmShort = date.getHours() < 12 ? 'AM' : 'PM'
      return `${hour12Short}:${minutes} ${ampmShort}`
    case 'h:mm:ss PM': // 如 "3:00:00 PM"
      const hour12Short2 = date.getHours() % 12 || 12
      const ampmShort2 = date.getHours() < 12 ? 'AM' : 'PM'
      return `${hour12Short2}:${minutes}:${seconds} ${ampmShort2}`

      // 默认格式
    default:
      return `${year}-${month}-${day} ${hours}:${minutes}`
  }
}

// 实现银行家舍入法的函数
export function bankersRounding (num, decimalPlaces) {
  const factor = Math.pow(10, decimalPlaces)
  const scaledNum = num * factor
  const integerPart = Math.floor(scaledNum)
  const fractionalPart = scaledNum - integerPart
  const digitToCheck = fractionalPart * 10

  if (digitToCheck < 5) {
    // 小于 5 舍去
    return integerPart / factor
  } else if (digitToCheck > 5) {
    // 大于 5 进位
    return (integerPart + 1) / factor
  } else {
    // 等于 5 的情况
    const remainingDigits = String(scaledNum).slice(String(integerPart).length + 2)
    if (remainingDigits.length > 0 && Number(remainingDigits) > 0) {
      // 5 后有非零数字，进位
      return (integerPart + 1) / factor
    } else {
      // 5 后全为零，看 5 前数字的奇偶性
      if (integerPart % 2 === 0) {
        // 偶数则舍去
        return integerPart / factor
      } else {
        // 奇数则进位
        return (integerPart + 1) / factor
      }
    }
  }
}

export function roundAmount (amount, modeindex, decimalPlaces = 2) {
  /* 3  'raw' - 直接保留小数位数（使用toFixed原生处理，含四舍五入）
    *  4 'round' - 不做处理，直接保留小数（toFixed原生）
    *  0 this.$store.getters.Rounding - 三进五，八进十（小数点后第二位：1-2舍，3-7进5，8-9进1位）
    *  1 'ceilFiveOrTen' - 向上取整，进五或十
    *  2 'ceilToTen' - 向上取整到小数点后一位
    */
  const modevalue = ['threeFiveEightTen', 'ceilFiveOrTen', 'ceilToTen', 'raw', 'round']
  const mode = modevalue[modeindex]
  // 确保输入为数字
  const num = parseFloat(amount)
  if (isNaN(num)) {
    throw new Error('Invalid amount: must be a number')
  }

  // 处理 decimalPlaces 非整数/负数情况
  decimalPlaces = Math.max(0, Math.floor(decimalPlaces))

  // 通用工具：使用toFixed保留小数并转为数字（修复精度问题）
  const toFixedNumber = (num, places) => {
    return parseFloat(num.toFixed(places))
  }

  // 新增：直接截断小数位的工具函数
  const truncateDecimal = (num, places) => {
    const factor = Math.pow(10, places)
    return Math.floor(num * factor) / factor
  }

  switch (mode) {
    case 'raw': // 直接保留小数位数（toFixed原生行为，含四舍五入）
      return toFixedNumber(num, decimalPlaces)

    case 'round': // 修改为直接截断小数位（不四舍五入）
      return truncateDecimal(num, decimalPlaces)

    case 'threeFiveEightTen':
      return threeFiveEightTenRound(num, decimalPlaces)

    case 'ceilFiveOrTen':
      return ceilFiveOrTenRound(num, decimalPlaces)

    case 'ceilToTen':
      return ceilToTenRound(num, decimalPlaces)

    default:
      throw new Error(`Unsupported round mode: ${mode}`)
  }
}

/**
 * 三进五，八进十舍入（小数点后第二位：1-2舍，3-7进5，8-9进1位）
 * @param {number} num - 原始金额
 * @param {number} decimalPlaces - 保留小数位数（默认2位）
 * @returns {number} 舍入后的金额
 */
export function threeFiveEightTenRound (num, decimalPlaces = 2) {
  const factor = Math.pow(10, decimalPlaces)
  const scaled = Math.round(num * factor) // 放大后取整，避免浮点数精度问题
  const secondDecimal = scaled % 10 // 获取小数点后第二位（放大后的值的个位数）

  let result
  if (secondDecimal >= 8) {
    // 8-9：进1位（如 12.39 → 12.40）
    result = (scaled - secondDecimal + 10) / factor
  } else if (secondDecimal >= 3) {
    // 3-7：进5（如 12.33 → 12.35）
    result = (scaled - secondDecimal + 5) / factor
  } else {
    // 1-2：舍去（如 12.32 → 12.30）
    result = (scaled - secondDecimal) / factor
  }

  // 修复精度并返回
  return parseFloat(result.toFixed(decimalPlaces))
}

/**
 * 向上取整，进五或十（1-50进5，51-99进10）
 * @param {number} num - 原始金额
 * @param {number} decimalPlaces - 保留小数位数（默认2位）
 * @returns {number} 舍入后的金额
 */
export function ceilFiveOrTenRound (num, decimalPlaces = 2) {
  const factor = Math.pow(10, decimalPlaces)
  const scaled = num * factor // 放大金额（如 12.34 → 1234，保留2位小数时）
  const remainder = scaled % factor // 余数（如 1234 → 34，即 0.34元）

  // 金额为整数时直接返回
  if (remainder === 0) return parseFloat(num.toFixed(decimalPlaces))

  const base = Math.floor(scaled / factor) // 整数部分（如 1234 → 12）
  if (remainder > 50) {
    // 余数 >50：向上取整到下一位整数（如 12.51 → 13.00）
    return parseFloat(((base + 1) * factor / factor).toFixed(decimalPlaces))
  } else {
    // 余数 ≤50：向上取整到最近的0.05（如 12.13 → 12.15，12.30 → 12.30）
    const nextFive = Math.ceil(remainder / 5) * 5 // 余数向上取5的倍数（34 → 35，49 → 50）
    return parseFloat(((base * factor + nextFive) / factor).toFixed(decimalPlaces))
  }
}

/**
 * 向上取整到小数点后一位（如 12.31 → 12.4，12.3 → 12.3）
 * @param {number} num - 原始金额
 * @returns {number} 舍入后的金额（固定1位小数）
 */
export function ceilToTenRound (num) {
  if (isNaN(num)) {
    throw new Error('输入必须为数字')
  }
  // 放大10倍后向上取整，再缩小10倍（如 12.31 → 123.1 → 124 → 12.4）
  const result = Math.ceil(num * 10) / 10
  // 固定1位小数并返回
  return parseFloat(result.toFixed(1))
}
// 截取两位小数，不做取舍
export function to2NoRound (num) {
  if (isNaN(num)) return 'NaN'

  const isNegative = num < 0
  const absoluteNum = Math.abs(num)
  const integerPart = Math.trunc(absoluteNum)
  const decimalPart = Math.trunc((absoluteNum - integerPart) * 100)

  const result = integerPart + '.' + decimalPart.toString().padStart(2, '0')
  return isNegative ? '-' + result : result
}
// 语言包全局过滤器
export function changelang (key, value) {
  try {
    // 检查key是否有效
    if (!key || typeof key !== 'string') {
      return key || ''
    }
    const langdataListStr = localStorage.getItem('langdataList')
    const langchangesize = localStorage.getItem('langId') || '2' // 默认英文

    // 检查langdataList是否存在且有效
    if (!langdataListStr) {
      return key
    }
    const raw = JSON.parse(langdataListStr)
    if (!Array.isArray(raw)) {
      return key
    }

    // 查找对应的翻译,若存在value则替换字段中的特殊字符
    for (const item of raw) {
      if (item && item.key === key) {
        if (value) {
          return (langchangesize === '1' ? item.L1 || key : item.L2 || key).replace('GAG', value)
        }
        return langchangesize === '1' ? item.L1 || key : item.L2 || key
      }
    }
    return key
  } catch (error) {
    return key
  }
}
export function readFile (filename, callback) {
  try {
    const fs = window.require('fs')
    var filename = filename
    const execPath = window.require('path').dirname(process.execPath)
    filename = execPath + '\\' + filename
    console.log('filename', filename)
    fs.readFile(filename, function (err, data) {
      console.log(err)
      // console.log('data', data.toString())
      try {
        const info = data.toString()
        const confList = info.split('\n')
        const confArray = new Array()
        console.log('confList', confList)
        for (let i = 0; i < confList.length; i++) {
          const one = confList[i]
          const key = one.substring(0, one.indexOf('=')).replace(/(\r\n)|(\n)|(\r)/g, '')
          const value = one.substring(one.indexOf('=') + 1).replace(/(\r\n)|(\n)|(\r)/g, '')
          confArray[key] = value
        }
        callback && callback(confArray)
      } catch (e) {
        // callback && callback()
        console.log(e)
      }
    })
  } catch (error) {
    console.log(error)
  }
}
export function writeFile (filename, data, callback) {
  try {
    const fs = window.require('fs')
    var filename = filename
    const execPath = window.require('path').dirname(process.execPath)
    filename = execPath + '\\' + filename
    console.log('filename', filename)
    fs.writeFile(filename, data, { flag: 'a', encoding: 'utf-8', mode: '0666' }, function (error) {
      if (error) {
        console.log('写入失败')
      } else {
        console.log('写入成功')
      }
    })
    // fs.readFile(filename, function(err, data){
    //   console.log(err)
    //   console.log('data', data.toString())
    //   try{
    //     var info = data.toString();
    //     var confList = info.split('\n');
    //     var confArray = new Array();
    //     console.log('confList', confList)
    //     for(let i=0;i<confList.length;i++){
    //       var one = confList[i];
    //       var key = one.substring(0,one.indexOf('=')).replace(/(\r\n)|(\n)|(\r)/g,'');
    //           var value = one.substring(one.indexOf('=')+1).replace(/(\r\n)|(\n)|(\r)/g,'');
    //       confArray[key] = value;
    //     }
    //     callback && callback(confArray)
    //   }catch(e){
    //     //callback && callback()
    //     console.log(e)
    //   }

    // })
  } catch (error) {
    console.log(error)
  }
}

export function showprice (num) {
  // 检查输入是否为有效数字
  if (num === null || num === undefined || isNaN(Number(num))) {
    return '0.00' // 默认为0.00，也可根据需求返回空字符串
  }

  // 转换为数字并保留两位小数
  return Number(num).toFixed(2)
}
